import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useSpring, useTransform } from 'framer-motion';
import {
  Play,
  Search,
  BarChart3,
  Brain,
  CheckCircle,
  Clock,
  Lock,
  ArrowRight,
  RefreshCw,
  Zap,
  Target,
  Users,
  Calendar,
  FileText,
  Sparkles,
  ChevronRight,
  Info,
  Star,
  Layers,
  TrendingUp,
  Settings,
  Award,
  Rocket
} from 'lucide-react';
import { workflowNodes } from '../../data/demoData';
import { WorkflowNode } from '../../types';

const WorkflowMap = () => {
  const [nodes, setNodes] = useState<WorkflowNode[]>(workflowNodes);
  const [selectedNode, setSelectedNode] = useState<WorkflowNode | null>(null);
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);
  const [completionAnimation, setCompletionAnimation] = useState<string | null>(null);
  const [workflowProgress, setWorkflowProgress] = useState<Record<string, boolean>>({
    'start': true,
    'research': true,
    'market-analysis': true,
    'problem-analysis': false,
    'triz-innovation': false,
    'feature-definition': false,
    'architecture': false,
    'prioritization': false,
    'timeline': false,
    'review': false,
    'iterate': false,
    'deploy': false
  });

  const canvasRef = useRef<HTMLDivElement>(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  // Track mouse movement for premium effects
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (canvasRef.current) {
        const rect = canvasRef.current.getBoundingClientRect();
        setMousePosition({
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
        });
      }
    };

    const canvas = canvasRef.current;
    if (canvas) {
      canvas.addEventListener('mousemove', handleMouseMove);
      return () => canvas.removeEventListener('mousemove', handleMouseMove);
    }
  }, []);

  // Synchronize node status with workflow progress
  useEffect(() => {
    setNodes(prevNodes => 
      prevNodes.map(node => {
        if (workflowProgress[node.id]) {
          return { ...node, status: 'completed' as const };
        }
        
        // Check if this node should be available
        const dependencyNode = prevNodes.find(n => n.connections.includes(node.id));
        if (dependencyNode && workflowProgress[dependencyNode.id]) {
          return { ...node, status: 'available' as const };
        }
        
        // Special case: start node should always be available
        if (node.id === 'start') {
          return { ...node, status: 'available' as const };
        }
        
        return { ...node, status: 'locked' as const };
      })
    );
  }, [workflowProgress]);

  const getNodeIcon = (type: string) => {
    switch (type) {
      case 'start': return Rocket;
      case 'process': return Layers;
      case 'ai': return Brain;
      case 'decision': return Target;
      case 'end': return Award;
      default: return FileText;
    }
  };

  const getNodeTheme = (type: string, status: string) => {
    if (status === 'completed') {
      return {
        gradient: 'from-emerald-400 via-emerald-500 to-emerald-600',
        shadow: 'shadow-emerald-500/30',
        glow: 'drop-shadow-[0_0_20px_rgba(16,185,129,0.4)]',
        ring: 'ring-emerald-400/20',
        bg: 'bg-emerald-500/10',
        border: 'border-emerald-400/30'
      };
    }
    
    if (status === 'available') {
      switch (type) {
        case 'start':
          return {
            gradient: 'from-blue-400 via-blue-500 to-indigo-600',
            shadow: 'shadow-blue-500/30',
            glow: 'drop-shadow-[0_0_20px_rgba(59,130,246,0.4)]',
            ring: 'ring-blue-400/20',
            bg: 'bg-blue-500/10',
            border: 'border-blue-400/30'
          };
        case 'ai':
          return {
            gradient: 'from-purple-400 via-purple-500 to-pink-600',
            shadow: 'shadow-purple-500/30',
            glow: 'drop-shadow-[0_0_20px_rgba(139,92,246,0.4)]',
            ring: 'ring-purple-400/20',
            bg: 'bg-purple-500/10',
            border: 'border-purple-400/30'
          };
        case 'decision':
          return {
            gradient: 'from-orange-400 via-orange-500 to-red-600',
            shadow: 'shadow-orange-500/30',
            glow: 'drop-shadow-[0_0_20px_rgba(249,115,22,0.4)]',
            ring: 'ring-orange-400/20',
            bg: 'bg-orange-500/10',
            border: 'border-orange-400/30'
          };
        case 'end':
          return {
            gradient: 'from-violet-400 via-violet-500 to-purple-600',
            shadow: 'shadow-violet-500/30',
            glow: 'drop-shadow-[0_0_20px_rgba(139,92,246,0.4)]',
            ring: 'ring-violet-400/20',
            bg: 'bg-violet-500/10',
            border: 'border-violet-400/30'
          };
        default:
          return {
            gradient: 'from-cyan-400 via-cyan-500 to-blue-600',
            shadow: 'shadow-cyan-500/30',
            glow: 'drop-shadow-[0_0_20px_rgba(6,182,212,0.4)]',
            ring: 'ring-cyan-400/20',
            bg: 'bg-cyan-500/10',
            border: 'border-cyan-400/30'
          };
      }
    }
    
    return {
      gradient: 'from-slate-300 via-slate-400 to-slate-500',
      shadow: 'shadow-slate-400/20',
      glow: 'drop-shadow-[0_0_10px_rgba(100,116,139,0.2)]',
      ring: 'ring-slate-400/10',
      bg: 'bg-slate-500/5',
      border: 'border-slate-400/20'
    };
  };

  const handleNodeClick = (node: WorkflowNode) => {
    if (node.status !== 'locked') {
      setSelectedNode(node);
    }
  };

  const completeNode = async (nodeId: string) => {
    setCompletionAnimation(nodeId);
    
    // Wait for animation
    setTimeout(() => {
      const newProgress = {
        ...workflowProgress,
        [nodeId]: true
      };
      
      setWorkflowProgress(newProgress);
      setCompletionAnimation(null);
      
      // Auto-unlock next nodes
      setNodes(prevNodes => 
        prevNodes.map(node => {
          if (node.id === nodeId) {
            return { ...node, status: 'completed' as const };
          }
          
          const dependencyNode = prevNodes.find(n => n.connections.includes(node.id));
          if (dependencyNode && newProgress[dependencyNode.id] && node.status === 'locked') {
            return { ...node, status: 'available' as const };
          }
          
          return node;
        })
      );
    }, 1000);
  };

  const resetWorkflow = () => {
    setWorkflowProgress({
      'start': true,
      'research': true,
      'market-analysis': true,
      'problem-analysis': false,
      'triz-innovation': false,
      'feature-definition': false,
      'architecture': false,
      'prioritization': false,
      'timeline': false,
      'review': false,
      'iterate': false,
      'deploy': false
    });
    
    setNodes(workflowNodes);
    setSelectedNode(null);
    setCompletionAnimation(null);
  };

  const completedCount = Object.values(workflowProgress).filter(Boolean).length;
  const progressPercentage = (completedCount / nodes.length) * 100;

  return (
    <div className="h-full bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900 relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_80%,rgba(120,119,198,0.1),transparent)] opacity-40"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(255,119,198,0.1),transparent)] opacity-40"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_40%_40%,rgba(59,130,246,0.1),transparent)] opacity-40"></div>
        
        {/* Floating Particles */}
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-white/20 rounded-full"
            initial={{
              x: Math.random() * window.innerWidth,
              y: Math.random() * window.innerHeight,
            }}
            animate={{
              x: Math.random() * window.innerWidth,
              y: Math.random() * window.innerHeight,
            }}
            transition={{
              duration: Math.random() * 20 + 20,
              repeat: Infinity,
              ease: "linear"
            }}
          />
        ))}
      </div>

      {/* Mouse Follower */}
      <motion.div
        className="absolute pointer-events-none z-10"
        animate={{
          x: mousePosition.x - 100,
          y: mousePosition.y - 100,
        }}
        transition={{ type: "spring", damping: 30, stiffness: 200 }}
      >
        <div className="w-48 h-48 bg-gradient-to-r from-blue-400/5 to-purple-400/5 rounded-full blur-xl"></div>
      </motion.div>

      <div className="relative z-20 p-8 h-full">
        <div className="max-w-7xl mx-auto h-full">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <div className="flex items-center justify-between mb-6">
              <div className="space-y-3">
                <div className="flex items-center space-x-4">
                  <div className="p-3 rounded-2xl bg-gradient-to-r from-blue-500/20 to-purple-500/20 backdrop-blur-xl border border-white/10">
                    <Sparkles className="w-8 h-8 text-blue-300" />
                  </div>
                  <div>
                    <h1 className="text-4xl font-bold bg-gradient-to-r from-white via-blue-100 to-purple-100 bg-clip-text text-transparent">
                      AI Workflow Engine
                    </h1>
                    <p className="text-slate-400 text-lg">
                      Navigate through intelligent roadmap creation
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center space-x-4">
                {/* Progress Stats */}
                <div className="bg-black/40 backdrop-blur-2xl rounded-2xl p-6 border border-white/10">
                  <div className="flex items-center space-x-4">
                    <div className="text-center">
                      <div className="text-3xl font-bold bg-gradient-to-r from-emerald-400 to-emerald-300 bg-clip-text text-transparent">
                        {completedCount}
                      </div>
                      <div className="text-xs text-slate-400 uppercase tracking-wider">Completed</div>
                    </div>
                    
                    <div className="w-px h-12 bg-gradient-to-b from-transparent via-white/20 to-transparent"></div>
                    
                    <div className="text-center">
                      <div className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-blue-300 bg-clip-text text-transparent">
                        {Math.round(progressPercentage)}%
                      </div>
                      <div className="text-xs text-slate-400 uppercase tracking-wider">Progress</div>
                    </div>
                    
                    <div className="w-20 h-20 relative">
                      <svg className="transform -rotate-90 w-20 h-20">
                        <circle
                          cx="40"
                          cy="40"
                          r="32"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="transparent"
                          className="text-slate-700"
                        />
                        <motion.circle
                          cx="40"
                          cy="40"
                          r="32"
                          stroke="url(#progressGradient)"
                          strokeWidth="4"
                          fill="transparent"
                          strokeLinecap="round"
                          initial={{ pathLength: 0 }}
                          animate={{ pathLength: progressPercentage / 100 }}
                          transition={{ duration: 1, ease: "easeInOut" }}
                          strokeDasharray="201"
                          strokeDashoffset="201"
                        />
                        <defs>
                          <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#3b82f6" />
                            <stop offset="50%" stopColor="#8b5cf6" />
                            <stop offset="100%" stopColor="#ec4899" />
                          </linearGradient>
                        </defs>
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Zap className="w-6 h-6 text-blue-400" />
                      </div>
                    </div>
                  </div>
                </div>
                
                <motion.button
                  whileHover={{ scale: 1.05, rotateZ: 180 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={resetWorkflow}
                  className="p-4 rounded-2xl bg-gradient-to-r from-slate-800/80 to-slate-700/80 backdrop-blur-xl border border-white/10 text-slate-300 hover:text-white transition-all duration-300 hover:shadow-lg hover:shadow-slate-500/20"
                >
                  <RefreshCw className="w-6 h-6" />
                </motion.button>
              </div>
            </div>
          </motion.div>

          {/* Main Content */}
          <div className="grid grid-cols-1 xl:grid-cols-4 gap-8 h-[calc(100vh-300px)]">
            {/* Workflow Canvas */}
            <div className="xl:col-span-3 relative">
              <div
                ref={canvasRef}
                className="relative w-full h-full bg-black/20 backdrop-blur-2xl rounded-3xl border border-white/10 overflow-hidden"
              >
                {/* Grid Pattern */}
                <div className="absolute inset-0">
                  <svg width="100%" height="100%" className="opacity-20">
                    <defs>
                      <pattern id="grid" width="50" height="50" patternUnits="userSpaceOnUse">
                        <path d="M 50 0 L 0 0 0 50" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="1"/>
                      </pattern>
                    </defs>
                    <rect width="100%" height="100%" fill="url(#grid)" />
                  </svg>
                </div>

                {/* Connection Lines */}
                <svg className="absolute inset-0 w-full h-full pointer-events-none">
                  <defs>
                    <linearGradient id="connectionGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="rgba(59,130,246,0.4)" />
                      <stop offset="50%" stopColor="rgba(139,92,246,0.6)" />
                      <stop offset="100%" stopColor="rgba(236,72,153,0.4)" />
                    </linearGradient>
                    <filter id="glow">
                      <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                      <feMerge> 
                        <feMergeNode in="coloredBlur"/>
                        <feMergeNode in="SourceGraphic"/>
                      </feMerge>
                    </filter>
                  </defs>
                  
                  {nodes.map(node => (
                    node.connections.map(targetId => {
                      const targetNode = nodes.find(n => n.id === targetId);
                      if (!targetNode) return null;
                      
                      const isActive = workflowProgress[node.id] || workflowProgress[targetId];
                      
                      return (
                        <motion.path
                          key={`${node.id}-${targetId}`}
                          d={`M ${node.position.x + 50} ${node.position.y + 50} Q ${(node.position.x + targetNode.position.x) / 2 + 50} ${node.position.y - 50} ${targetNode.position.x + 50} ${targetNode.position.y + 50}`}
                          stroke={isActive ? "url(#connectionGradient)" : "rgba(100,116,139,0.3)"}
                          strokeWidth={isActive ? "3" : "2"}
                          fill="none"
                          filter={isActive ? "url(#glow)" : "none"}
                          initial={{ pathLength: 0 }}
                          animate={{ pathLength: 1 }}
                          transition={{ duration: 2, delay: 0.5 }}
                          className="transition-all duration-500"
                        />
                      );
                    })
                  )).flat()}
                </svg>

                {/* Workflow Nodes */}
                {nodes.map((node, index) => {
                  const Icon = getNodeIcon(node.type);
                  const theme = getNodeTheme(node.type, node.status);
                  const isCompleted = workflowProgress[node.id];
                  const isHovered = hoveredNode === node.id;
                  const isAnimating = completionAnimation === node.id;
                  
                  return (
                    <motion.div
                      key={node.id}
                      className="absolute"
                      style={{
                        left: node.position.x,
                        top: node.position.y,
                      }}
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ delay: index * 0.1, type: "spring", bounce: 0.4 }}
                      onMouseEnter={() => setHoveredNode(node.id)}
                      onMouseLeave={() => setHoveredNode(null)}
                    >
                      {/* Node Glow Effect */}
                      <motion.div
                        className={`absolute inset-0 rounded-full blur-xl ${theme.bg} ${theme.glow}`}
                        animate={{
                          scale: isHovered ? 1.3 : isCompleted ? 1.1 : 1,
                          opacity: isHovered ? 0.8 : isCompleted ? 0.6 : 0.3,
                        }}
                        transition={{ duration: 0.3 }}
                      />
                      
                      {/* Main Node */}
                      <motion.div
                        className={`relative w-24 h-24 rounded-full cursor-pointer ${
                          node.status === 'locked' ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                        whileHover={node.status !== 'locked' ? { scale: 1.1 } : {}}
                        whileTap={node.status !== 'locked' ? { scale: 0.95 } : {}}
                        onClick={() => handleNodeClick(node)}
                        animate={{
                          scale: isAnimating ? [1, 1.2, 1] : 1,
                        }}
                        transition={{
                          duration: isAnimating ? 1 : 0.3,
                          ease: "easeInOut"
                        }}
                      >
                        {/* Node Background */}
                        <div className={`absolute inset-0 rounded-full bg-gradient-to-br ${theme.gradient} ${theme.shadow} shadow-2xl`} />
                        
                        {/* Node Border */}
                        <div className={`absolute inset-0 rounded-full border-2 ${theme.border}`} />
                        
                        {/* Completion Ring */}
                        {isCompleted && (
                          <motion.div
                            className="absolute -inset-2 rounded-full border-4 border-emerald-400/50"
                            initial={{ scale: 0, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ duration: 0.5 }}
                          />
                        )}
                        
                        {/* Node Content */}
                        <div className="relative z-10 w-full h-full flex items-center justify-center">
                          {node.status === 'locked' ? (
                            <Lock className="w-8 h-8 text-white/70" />
                          ) : isCompleted ? (
                            <motion.div
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              transition={{ type: "spring", bounce: 0.6 }}
                            >
                              <CheckCircle className="w-8 h-8 text-white" />
                            </motion.div>
                          ) : (
                            <Icon className="w-8 h-8 text-white" />
                          )}
                        </div>
                        
                        {/* Pulse Animation for Available Nodes */}
                        {node.status === 'available' && !isCompleted && (
                          <motion.div
                            className={`absolute inset-0 rounded-full bg-gradient-to-br ${theme.gradient} opacity-40`}
                            animate={{ scale: [1, 1.3, 1], opacity: [0.4, 0, 0.4] }}
                            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                          />
                        )}
                      </motion.div>
                      
                      {/* Node Label */}
                      <motion.div
                        className="absolute top-28 left-1/2 transform -translate-x-1/2 text-center"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 + 0.2 }}
                      >
                        <div className="px-3 py-1 rounded-lg bg-black/40 backdrop-blur-xl border border-white/10">
                          <div className="text-sm font-medium text-white whitespace-nowrap">
                            {node.name.split(' ').slice(0, 2).join(' ')}
                          </div>
                        </div>
                      </motion.div>
                      
                      {/* Hover Information */}
                      <AnimatePresence>
                        {isHovered && node.status !== 'locked' && (
                          <motion.div
                            initial={{ opacity: 0, scale: 0.8, y: 10 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.8, y: 10 }}
                            className="absolute top-32 left-1/2 transform -translate-x-1/2 z-20"
                          >
                            <div className="bg-black/80 backdrop-blur-2xl rounded-xl p-3 border border-white/20 max-w-xs">
                              <div className="text-xs text-slate-300">
                                {node.description}
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  );
                })}
              </div>
            </div>

            {/* Side Panel */}
            <div className="space-y-6">
              <AnimatePresence mode="wait">
                {selectedNode ? (
                  <motion.div
                    key="node-details"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="bg-black/40 backdrop-blur-2xl rounded-3xl border border-white/10 p-8 h-full overflow-auto"
                  >
                    <div className="space-y-6">
                      {/* Node Header */}
                      <div className="text-center">
                        <div className={`w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br ${getNodeTheme(selectedNode.type, selectedNode.status).gradient} flex items-center justify-center mb-4 shadow-2xl`}>
                          {React.createElement(getNodeIcon(selectedNode.type), { 
                            className: "w-8 h-8 text-white" 
                          })}
                        </div>
                        
                        <h3 className="text-2xl font-bold text-white mb-2">
                          {selectedNode.name}
                        </h3>
                        
                        <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                          selectedNode.status === 'completed' ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30' :
                          selectedNode.status === 'available' ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' :
                          'bg-slate-500/20 text-slate-300 border border-slate-500/30'
                        }`}>
                          {selectedNode.status === 'completed' ? (
                            <>
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Completed
                            </>
                          ) : selectedNode.status === 'available' ? (
                            <>
                              <Clock className="w-4 h-4 mr-2" />
                              Available
                            </>
                          ) : (
                            <>
                              <Lock className="w-4 h-4 mr-2" />
                              Locked
                            </>
                          )}
                        </div>
                      </div>
                      
                      {/* Description */}
                      <div className="space-y-4">
                        <div>
                          <h4 className="text-lg font-semibold text-white mb-3 flex items-center">
                            <Info className="w-5 h-5 mr-2 text-blue-400" />
                            Overview
                          </h4>
                          <p className="text-slate-300 leading-relaxed">
                            {selectedNode.description}
                          </p>
                        </div>
                        
                        <div>
                          <h4 className="text-lg font-semibold text-white mb-3 flex items-center">
                            <Target className="w-5 h-5 mr-2 text-orange-400" />
                            Details
                          </h4>
                          <p className="text-slate-300 leading-relaxed">
                            {selectedNode.details}
                          </p>
                        </div>
                        
                        {selectedNode.nextSteps.length > 0 && (
                          <div>
                            <h4 className="text-lg font-semibold text-white mb-3 flex items-center">
                              <ArrowRight className="w-5 h-5 mr-2 text-green-400" />
                              Next Steps
                            </h4>
                            <ul className="space-y-2">
                              {selectedNode.nextSteps.map((step, index) => (
                                <motion.li
                                  key={index}
                                  initial={{ opacity: 0, x: -10 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{ delay: index * 0.1 }}
                                  className="text-slate-300 flex items-start space-x-2"
                                >
                                  <ChevronRight className="w-4 h-4 mt-0.5 text-blue-400 flex-shrink-0" />
                                  <span>{step}</span>
                                </motion.li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                      
                      {/* Action Button */}
                      {selectedNode.status === 'available' && !workflowProgress[selectedNode.id] && (
                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => completeNode(selectedNode.id)}
                          disabled={completionAnimation === selectedNode.id}
                          className="w-full py-4 px-6 rounded-2xl bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold text-lg shadow-2xl hover:shadow-blue-500/25 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                        >
                          {completionAnimation === selectedNode.id ? (
                            <>
                              <motion.div
                                animate={{ rotate: 360 }}
                                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                              >
                                <Zap className="w-5 h-5" />
                              </motion.div>
                              <span>Completing...</span>
                            </>
                          ) : (
                            <>
                              <Star className="w-5 h-5" />
                              <span>Complete Step</span>
                            </>
                          )}
                        </motion.button>
                      )}
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key="workflow-overview"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="bg-black/40 backdrop-blur-2xl rounded-3xl border border-white/10 p-8 h-full"
                  >
                    <div className="text-center space-y-6">
                      <div>
                        <div className="w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center mb-4 shadow-2xl">
                          <Brain className="w-10 h-10 text-white" />
                        </div>
                        
                        <h3 className="text-2xl font-bold text-white mb-2">
                          AI Workflow Engine
                        </h3>
                        <p className="text-slate-400">
                          Click on any node to explore the workflow step in detail
                        </p>
                      </div>
                      
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4">
                            <div className="text-2xl font-bold text-emerald-400">
                              {completedCount}
                            </div>
                            <div className="text-sm text-emerald-300">Completed</div>
                          </div>
                          
                          <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
                            <div className="text-2xl font-bold text-blue-400">
                              {nodes.filter(n => n.status === 'available').length}
                            </div>
                            <div className="text-sm text-blue-300">Available</div>
                          </div>
                        </div>
                        
                        <div className="space-y-3">
                          <h4 className="text-lg font-semibold text-white">Legend</h4>
                          
                          <div className="space-y-2 text-sm">
                            <div className="flex items-center space-x-3">
                              <div className="w-6 h-6 rounded-full bg-gradient-to-r from-blue-400 to-indigo-600 flex items-center justify-center">
                                <Rocket className="w-3 h-3 text-white" />
                              </div>
                              <span className="text-slate-300">Start Process</span>
                            </div>
                            
                            <div className="flex items-center space-x-3">
                              <div className="w-6 h-6 rounded-full bg-gradient-to-r from-purple-400 to-pink-600 flex items-center justify-center">
                                <Brain className="w-3 h-3 text-white" />
                              </div>
                              <span className="text-slate-300">AI-Powered</span>
                            </div>
                            
                            <div className="flex items-center space-x-3">
                              <div className="w-6 h-6 rounded-full bg-gradient-to-r from-orange-400 to-red-600 flex items-center justify-center">
                                <Target className="w-3 h-3 text-white" />
                              </div>
                              <span className="text-slate-300">Decision Point</span>
                            </div>
                            
                            <div className="flex items-center space-x-3">
                              <div className="w-6 h-6 rounded-full bg-gradient-to-r from-emerald-400 to-emerald-600 flex items-center justify-center">
                                <CheckCircle className="w-3 h-3 text-white" />
                              </div>
                              <span className="text-slate-300">Completed</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkflowMap;